import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../lib/cn";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./hover-card";
import { ShortcutKeys } from "./shortcut-keys";
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from "./sidebar";

/**
 * Rows of the contextual sidebar: the second tier of the two-tier sidebar,
 * beside the app rail (`app-rail`).
 *
 * The -mx-1 cancels the nav column's px-1 when expanded and offsets the
 * asymmetric pl-3/pr-1 collapsed gutters.
 */
export const SIDEBAR_NAV_ITEM_CLASS = "group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full -mx-1";
export const SIDEBAR_NAV_BUTTON_CLASS = "h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 data-[active=true]:bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:data-[active=true]:bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white cursor-pointer";

/** Icon button in a contextual sidebar's header (search, collapse). */
export const SIDEBAR_HEADER_ACTION_CLASS = "h-8 w-8 rounded-lg p-1.5 text-neutral-500 hover:bg-black/[0.04] hover:text-neutral-900 hover:!border-transparent dark:text-neutral-400 dark:hover:bg-white/[0.04] dark:hover:text-white dark:hover:!border-transparent cursor-pointer";
export type SidebarNavSubItem = {
  name: string;
  href: string;
  current: boolean;
  icon: React.ReactNode;
  onMouseEnter?: () => void;
  shortcut?: string;
};

/** Props the hover card hands to `renderLink`; spread them onto the app's link. */
export type SidebarNavLinkProps = {
  href: string;
  className: string;
  children: React.ReactNode;
  "data-highlighted": boolean;
  onMouseEnter?: () => void;
  onPointerEnter: (event: React.PointerEvent<HTMLAnchorElement>) => void;
  onPointerLeave: () => void;
  onClick: () => void;
};
export type SidebarHoverCardMenuItemProps<T extends SidebarNavSubItem> = {
  name: string;
  icon: React.ReactNode;
  subItems: T[];
  /** Renders each sub-page link with the app's router link, e.g. Next's `Link` or Inertia's. */
  renderLink: (props: SidebarNavLinkProps, subItem: T) => React.ReactNode;
};

// Opening one row's card closes its siblings; the trigger stops pointerdown
// propagation, so Radix's outside-click dismissal can't do it on mobile.
const OPEN_EVENT = "sidebar-hover-card-open";

/**
 * Contextual-sidebar row that opens its sub-pages in a hover card to the
 * right (a tap toggle on mobile), e.g. Run History → Jobs / Workflows. The
 * row reads active whenever one of its sub-items is.
 */
export function SidebarHoverCardMenuItem(t0) {
  const $ = _c(67);
  const {
    name,
    icon,
    subItems,
    renderLink
  } = t0;
  const {
    isMobile
  } = useSidebar();
  const [open, setOpenState] = React.useState(false);
  const id = React.useId();
  const focusFirstLink = React.useRef(false);
  const [highlighted, setHighlighted] = React.useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = next => {
      setOpenState(next);
      if (!next) {
        setHighlighted(null);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const setOpen = t1;
  const triggerRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const isOwnNode = _temp;
  let t2;
  let t3;
  if ($[1] !== id || $[2] !== open) {
    t2 = () => {
      if (!open) {
        return;
      }
      window.dispatchEvent(new CustomEvent(OPEN_EVENT, {
        detail: id
      }));
      const closeIfOther = e => {
        if ((e as CustomEvent<string>).detail !== id) {
          setOpen(false);
        }
      };
      window.addEventListener(OPEN_EVENT, closeIfOther);
      return () => window.removeEventListener(OPEN_EVENT, closeIfOther);
    };
    t3 = [open, id];
    $[1] = id;
    $[2] = open;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  React.useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = node => {
      if (!node || !focusFirstLink.current) {
        return;
      }
      focusFirstLink.current = false;
      requestAnimationFrame(() => node.querySelector("a")?.focus());
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const focusFirstLinkOnMount = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_0 => {
      const forward = e_0.key === "ArrowDown" || e_0.key === "Tab" && !e_0.shiftKey;
      const back = e_0.key === "ArrowUp" || e_0.key === "Tab" && e_0.shiftKey;
      if (!forward && !back) {
        return;
      }
      const links = Array.from(e_0.currentTarget.querySelectorAll("a"));
      const index = links.findIndex(_temp2);
      if (index === -1) {
        return;
      }
      const next_0 = links[index + (forward ? 1 : -1)];
      if (next_0) {
        e_0.preventDefault();
        next_0.focus();
        return;
      }
      if (e_0.key !== "Tab") {
        e_0.preventDefault();
        return;
      }
      setOpen(false);
      triggerRef.current?.focus();
      if (!(e_0.key === "Tab" && forward)) {
        e_0.preventDefault();
      }
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleCardKeyDown = t5;
  let t6;
  if ($[7] !== isMobile) {
    t6 = next_1 => {
      if (isMobile && !next_1) {
        setOpen(false);
      }
    };
    $[7] = isMobile;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== subItems) {
    t7 = subItems.some(_temp3);
    $[9] = subItems;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t10;
  let t8;
  let t9;
  if ($[11] !== isMobile) {
    t8 = () => {
      if (isMobile) {
        return;
      }
      setOpen(true);
    };
    t9 = e_1 => {
      if (isMobile) {
        return;
      }
      if (isOwnNode(e_1.relatedTarget, contentRef)) {
        return;
      }
      setOpen(false);
    };
    t10 = e_2 => {
      e_2.stopPropagation();
      if (isMobile) {
        return;
      }
      setOpen(true);
    };
    $[11] = isMobile;
    $[12] = t10;
    $[13] = t8;
    $[14] = t9;
  } else {
    t10 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  let t11;
  if ($[15] !== open) {
    t11 = e_3 => {
      if (e_3.key !== "Escape" || !open) {
        return;
      }
      e_3.preventDefault();
      setOpen(false);
    };
    $[15] = open;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== isMobile || $[18] !== open) {
    t12 = e_4 => {
      if (!isMobile && e_4.detail === 0) {
        e_4.preventDefault();
        focusFirstLink.current = !open;
        setOpen(!open);
        return;
      }
      if (!isMobile) {
        return;
      }
      e_4.preventDefault();
      e_4.stopPropagation();
      setOpen(!open);
    };
    $[17] = isMobile;
    $[18] = open;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== icon) {
    t13 = <div className="relative">{icon}</div>;
    $[20] = icon;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== name) {
    t14 = <span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{name}</span>;
    $[22] = name;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ChevronRight className="ml-auto size-[18px] text-neutral-500 dark:text-neutral-400 group-data-[state=collapsed]:hidden" />;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== name || $[26] !== open || $[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
    t16 = <HoverCardTrigger asChild={true}><SidebarMenuButton ref={triggerRef} aria-label={name} aria-expanded={open} aria-haspopup="menu" isActive={t7} data-hovered={open} className="h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:!bg-black/[0.04] hover:text-neutral-950 data-[hovered=true]:!bg-black/[0.04] data-[hovered=true]:text-neutral-950 data-[active=true]:!bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[hovered=true]:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:!bg-white/[0.04] dark:hover:text-white dark:data-[hovered=true]:!bg-white/[0.04] dark:data-[hovered=true]:text-white dark:data-[active=true]:!bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[hovered=true]:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white data-[hovered=true]:focus-visible:!border-transparent dark:data-[hovered=true]:focus-visible:!border-transparent cursor-pointer" onPointerEnter={t8} onPointerLeave={t9} onPointerDownCapture={t10} onKeyDown={t11} onClick={t12}>{t13}{t14}{t15}</SidebarMenuButton></HoverCardTrigger>;
    $[25] = name;
    $[26] = open;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  let t18;
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setOpen(false);
    t18 = () => setHighlighted(null);
    t19 = () => {
      setOpen(false);
      triggerRef.current?.focus();
    };
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
  } else {
    t17 = $[36];
    t18 = $[37];
    t19 = $[38];
  }
  const t20 = isMobile ? "bottom" : "right";
  const t21 = !isMobile && "-translate-y-[5px]";
  let t22;
  if ($[39] !== t21) {
    t22 = cn("w-48 p-1 transition-none", t21);
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== isMobile) {
    t23 = () => {
      if (isMobile) {
        return;
      }
      setOpen(true);
    };
    $[41] = isMobile;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== isMobile) {
    t24 = e_5 => {
      if (isMobile) {
        return;
      }
      if (isOwnNode(e_5.relatedTarget, triggerRef)) {
        return;
      }
      setOpen(false);
    };
    $[43] = isMobile;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== highlighted || $[46] !== isMobile || $[47] !== renderLink || $[48] !== subItems) {
    let t26;
    if ($[50] !== highlighted || $[51] !== isMobile || $[52] !== renderLink) {
      t26 = subItem_0 => <React.Fragment key={subItem_0.name}>{renderLink({
          href: subItem_0.href,
          onMouseEnter: subItem_0.onMouseEnter,
          "data-highlighted": highlighted === subItem_0.name,
          onPointerEnter: e_6 => {
            if (e_6.pointerType === "touch") {
              return;
            }
            const items = e_6.currentTarget.closest("[data-hover-card-items]");
            if (items?.contains(document.activeElement)) {
              e_6.currentTarget.focus({
                preventScroll: true
              });
            } else {
              setHighlighted(subItem_0.name);
            }
          },
          onPointerLeave: () => setHighlighted(null),
          onClick: () => {
            if (isMobile) {
              setOpen(false);
            }
          },
          className: cn("flex items-center gap-2 rounded-md px-2 py-1.5 type-body-s-regular cursor-pointer transition-none outline-none", "text-neutral-800 [&_svg]:text-neutral-600 dark:text-neutral-300 dark:[&_svg]:text-neutral-400", subItem_0.current ? "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white focus:bg-black/[0.08] data-[highlighted=true]:bg-black/[0.08] dark:focus:bg-white/10 dark:data-[highlighted=true]:bg-white/10" : "focus:bg-neutral-50 focus:text-neutral-950 focus:[&_svg]:text-neutral-900 data-[highlighted=true]:bg-neutral-50 data-[highlighted=true]:text-neutral-950 data-[highlighted=true]:[&_svg]:text-neutral-900 dark:focus:bg-[#444545] dark:focus:text-white dark:focus:[&_svg]:text-white dark:data-[highlighted=true]:bg-[#444545] dark:data-[highlighted=true]:text-white dark:data-[highlighted=true]:[&_svg]:text-white"),
          children: <>{subItem_0.icon}<span>{subItem_0.name}</span>{subItem_0.shortcut ? <ShortcutKeys keys={[subItem_0.shortcut]} className="ml-auto" /> : null}</>
        }, subItem_0)}</React.Fragment>;
      $[50] = highlighted;
      $[51] = isMobile;
      $[52] = renderLink;
      $[53] = t26;
    } else {
      t26 = $[53];
    }
    t25 = subItems.map(t26);
    $[45] = highlighted;
    $[46] = isMobile;
    $[47] = renderLink;
    $[48] = subItems;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[54] !== t25) {
    t26 = <div ref={focusFirstLinkOnMount} data-hover-card-items={true} className="flex flex-col gap-0.5">{t25}</div>;
    $[54] = t25;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t20 || $[57] !== t22 || $[58] !== t23 || $[59] !== t24 || $[60] !== t26) {
    t27 = <HoverCardContent ref={contentRef} onPointerDownOutside={t17} onFocus={t18} onEscapeKeyDown={t19} side={t20} align="start" sideOffset={8} className={t22} onPointerEnter={t23} onKeyDown={handleCardKeyDown} onPointerLeave={t24}>{t26}</HoverCardContent>;
    $[56] = t20;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== open || $[63] !== t16 || $[64] !== t27 || $[65] !== t6) {
    t28 = <SidebarMenuItem className={SIDEBAR_NAV_ITEM_CLASS}><HoverCard open={open} openDelay={0} closeDelay={0} onOpenChange={t6}>{t16}{t27}</HoverCard></SidebarMenuItem>;
    $[62] = open;
    $[63] = t16;
    $[64] = t27;
    $[65] = t6;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  return t28;
}
function _temp3(subItem) {
  return subItem.current;
}
function _temp2(link) {
  return link === document.activeElement;
}
function _temp(target, ref) {
  return target instanceof Node && !!ref.current?.contains(target);
}